import { TableCellTypes } from '@carboledger/ui';
import { makeUnitTypeCell, makeUnitScopedCell, renderDiscrepanciesCell } from '@/components/common';
import { ORDER_DISPLAY_STATUS_TONE } from '@/config/statusTones';
import { buildOrderDisplayStatusVariants } from '@/config/statuses';
import { buildRowUnitOptions, unitTypeLabel } from './outgoingShipmentUnits';

// Outgoing shipment details ▸ Linked orders tab.

// The trailing attribute group is the only part the design lets you pin.
const PINNABLE = new Set([
  'tolerance',
  'maxGhgEmissions',
  'minGhgSavings',
  'rawMaterialType',
  'countryOfOrigin',
  'feedstockCategory',
  'allocationPreference',
  'dispatchBy',
  'discrepancies',
]);

const renderUnitType = makeUnitTypeCell((t, row) => buildRowUnitOptions(t, row.byUnit));

// Width is per-column so a long header renders in full rather than truncating.
const textColumn = (id, header, width = 200) => ({
  id,
  header,
  type: TableCellTypes.text,
  width,
  sortable: true,
  pinnable: PINNABLE.has(id),
});

const quantityColumn = (id, header, width = 200) => ({
  id,
  header,
  type: TableCellTypes.custom,
  width,
  sortable: true,
  pinnable: PINNABLE.has(id),
  cell: makeUnitScopedCell(id),
});

export const buildShipmentLinkedOrdersColumns = (t, { unitHeaderSelect }) => [
  textColumn('serialNo', t('FIELD_SERIAL_NO'), 90),
  textColumn('orderNo', t('ORDERS_COL_ORDER_NO'), 150),
  {
    id: 'status',
    header: t('FIELD_STATUS'),
    type: TableCellTypes.statusDot,
    width: 160,
    sortable: true,
    variantMap: buildOrderDisplayStatusVariants(t, ORDER_DISPLAY_STATUS_TONE),
  },
  textColumn('allocatedGhgSavings', t('OUTGOING_SHIPMENTS_DETAIL_COL_ALLOCATED_GHG_SAVINGS'), 280),
  {
    id: 'unitType',
    header: t('FIELD_UNIT_TYPE', { unit: unitTypeLabel(t, unitHeaderSelect.value) }),
    type: TableCellTypes.custom,
    width: 252,
    sortable: true,
    cell: renderUnitType,
    headerSelect: unitHeaderSelect,
  },
  quantityColumn(
    'assignedQuantity',
    t('OUTGOING_SHIPMENTS_DETAIL_COL_ASSIGNED_THIS_SHIPMENT'),
    250,
  ),
  quantityColumn('contractedQuantity', t('FIELD_CONTRACTED_QUANTITY'), 210),
  quantityColumn('shippedQuantity', t('FIELD_SHIPPED_QUANTITY')),
  quantityColumn('remainingToShipQuantity', t('ORDERS_COL_REMAINING_TO_SHIP'), 250),
  quantityColumn('allocatedQuantity', t('FIELD_ALLOCATED_QUANTITY')),
  quantityColumn('remainingToAllocateQuantity', t('FIELD_REMAINING_TO_ALLOCATE_QUANTITY'), 280),
  textColumn('tolerance', t('ORDERS_COL_TOLERANCE'), 160),
  textColumn('maxGhgEmissions', t('ORDERS_COL_MAX_GHG_EMISSIONS'), 220),
  textColumn('minGhgSavings', t('ORDERS_COL_MIN_GHG_SAVINGS'), 250),
  textColumn('rawMaterialType', t('FIELD_RAW_MATERIAL_TYPE')),
  textColumn('countryOfOrigin', t('FIELD_COUNTRY_OF_ORIGIN')),
  textColumn('feedstockCategory', t('FIELD_FEEDSTOCK_CATEGORY')),
  textColumn('allocationPreference', t('FIELD_ALLOCATION_PREFERENCE'), 220),
  textColumn('dispatchBy', t('FIELD_DISPATCH_BY'), 160),
  {
    id: 'discrepancies',
    header: t('FIELD_DISCREPANCIES'),
    type: TableCellTypes.custom,
    width: 200,
    sortable: true,
    pinnable: true,
    // Non-interactive: PendingOrder carries a 0/1 flag, not an itemized list to open.
    cell: renderDiscrepanciesCell,
  },
];
